import React, { useState } from 'react'
import {
  Card,
  Table,
  Pagination,
  Button,
  Space,
  DatePicker,
  Typography,
  Row,
  Col,
  Switch,
  Divider,
  Select,
} from 'antd'

import {
  DownloadOutlined,
  FilterOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import useOrganizationReport from '../Hooks/Reports/useOrganizationReport'

const { RangePicker } = DatePicker
const { Title, Text } = Typography

export default function OrganizationReport() {
  const {
    data,
    loading,
    pageNumber,
    pageSize,
    totalCount,
    orderByLatest,
    filters,
    statusOptions,
    getStatusLabel,
    setPageNumber,
    setPageSize,
    setFilters,
    setOrderByLatest,
    fetchOrganizationReport,
    downloadOrganizationReport,
  } = useOrganizationReport()

  console.log("????????????????????????data of organizations ",data);

// --- inside component ---
const [localDateRange, setLocalDateRange] = useState(
  filters.FromDate && filters.ToDate
    ? [dayjs(filters.FromDate), dayjs(filters.ToDate)]
    : null
);

// Apply filters
const handleApplyFilters = () => {
  const newFilters = { ...filters };

  if (localDateRange) {
    newFilters.FromDate = localDateRange[0].toDate();
    newFilters.ToDate = localDateRange[1].toDate();
  } else {
    delete newFilters.FromDate;
    delete newFilters.ToDate;
  }

  setFilters(newFilters);
  setPageNumber(1); // reset to first page
  fetchOrganizationReport();
};

// Reset filters
const handleResetFilters = () => {
  setLocalDateRange(null);
  resetFilters();
  fetchOrganizationReport();
};



  const toggleSortOrder = () => {
    setOrderByLatest(prev => !prev)
  }

  const resetFilters = () => {
    setFilters({})
    setOrderByLatest(true)
    setPageNumber(1)
  }

  const columns = [
    {
      title: 'Organization',
      fixed: 'left',
      render: (_, record) => (
        <div>
          <Text strong>{record.name}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            ID: {record.organizationId?.slice(0, 8)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Location',
      render: (_, record) => (
        <Space>
          <EnvironmentOutlined />
          <Text>
            {[record.city, record.state, record.country]
              .filter(Boolean)
              .join(', ') || 'N/A'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Specialty Area',
      dataIndex: 'specialtyAreaName',
      render: v => v || '—',
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      render: v => (v ? 'Yes' : 'No'),
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      render: d =>
        d ? dayjs(d).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (value) => getStatusLabel(value),
    }

  ]

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            Organization Report
          </Title>
          <Text type="secondary">
            Overview of registered organizations
          </Text>
        </div>
        <Space>

          <Button
            onClick={() => window.history.back()} // or navigate('/') if using react-router
          >
            Back
          </Button>

          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={downloadOrganizationReport}
            loading={loading}
          >
            Download
          </Button>
        </Space>
      </div>


      {/* Filters */}
      <Card
        title={
          <Space>
            <FilterOutlined />
            Filters
          </Space>
        }
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 16]}>
          {/* Date Range */}
          <Col xs={24} md={12} lg={8}>
            <Text strong>Registered Between Dates</Text>
          <RangePicker
  style={{ width: '100%', marginTop: 6 }}
  format="MM/DD/YYYY" // US format
  value={localDateRange}
  onChange={(dates) => setLocalDateRange(dates)}
/>

          </Col>

          {/* Status Combo */}
          <Col xs={24} md={12} lg={6}>
            <Text strong>Status</Text>
            <Select
              allowClear
              placeholder="Select status"
              style={{ width: '100%', marginTop: 6 }}
              value={filters.Status}
              onChange={(value) => {
                if (value !== undefined) {
                  setFilters({ ...filters, Status: value });
                } else {
                  const { Status, ...rest } = filters;
                  setFilters(rest);
                }
              }}
            >
              {statusOptions.map(opt => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Col>

          {/* Latest Toggle */}
          <Col xs={24} md={12} lg={4}>
            <Text strong>Sort Order</Text>
            <div style={{ marginTop: 10 }}>
              <Switch
                checked={orderByLatest}
                onChange={toggleSortOrder}
                checkedChildren="Latest"
                unCheckedChildren="Oldest"
              />
            </div>
          </Col>
        </Row>

        {/* ✅ Buttons moved to bottom */}
        <Divider style={{ marginTop: 24, marginBottom: 16 }} />
       <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
  <Button icon={<ReloadOutlined />} onClick={handleResetFilters} disabled={loading}>
    Reset
  </Button>
  <Button type="primary" onClick={handleApplyFilters} loading={loading}>
    Apply
  </Button>
</Space>

      </Card>



      {/* Table */}
      <Card>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
          scroll={{ x: 1100 }}
        />

        <Divider />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text type="secondary">
            Page {pageNumber} • {data.length} of {totalCount} organizations
          </Text>

          <Pagination
            current={pageNumber}
            pageSize={pageSize}
            total={totalCount}
            showSizeChanger
            onChange={(page, size) => {
              setPageNumber(page)
              setPageSize(size)
            }}
          />
        </div>
      </Card>
    </div>
  )
}
